import { useEffect } from 'react';
import { useInfiniteQuery, useQuery, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { addPhDays, todayInPH } from '@/lib/phDate';
import { daysBetween, type Crop } from '@/lib/crop';
import { format, parseISO } from 'date-fns';
import { ALL_SLOTS, GRID_ROWS, paramByKey, tierFor } from '@/lib/waterModel';
import { alkalinityTier, calMagTier, formatPair } from '@/lib/pairs';
import { useFarmThresholds } from '@/hooks/useFarmThresholds';
import type { ReadingSlot } from '@/types/entry';
import { readingToValues } from '@/components/entry/adaptive/entryData';
import { checkDay } from './waterChecks';
import type {
  ApplicationResponse,
  ApplicationSummary,
  CycleMilestoneResponse,
  DailyFeedingSummary,
  EditHistoryResponse,
  FarmProductResponse,
  FeedingResponse,
  HarvestResponse,
  SamplingResponse,
  WaterCheckResponse,
  WaterReadingResponse,
} from '@/types/generated/openapi';

interface ListResp<T> {
  data: T[];
}

export function useCropReadingsOn(farmId: string, cropId: string, date: string) {
  return useQuery({
    queryKey: qk.readings.day(farmId, cropId, date),
    queryFn: () =>
      api
        .get<ListResp<WaterReadingResponse>>(`/api/v1/farms/${farmId}/readings`, {
          params: { cycle_id: cropId, start_date: date, end_date: date, limit: '100' },
        })
        .then((r) => r.data ?? []),
  });
}

export function useCropFeedingsOn(farmId: string, cropId: string, date: string) {
  return useQuery({
    queryKey: qk.feedings.day(farmId, cropId, date),
    queryFn: () =>
      api
        .get<ListResp<FeedingResponse>>(`/api/v1/farms/${farmId}/feedings`, {
          params: { cycle_id: cropId, start_date: date, end_date: date, limit: '100' },
        })
        .then((r) => r.data ?? []),
  });
}

/** A crop holds ~10–20 samplings, well inside one page. */
export function useCropSamplings(farmId: string, cropId: string) {
  return useQuery({
    queryKey: qk.samplings.list(farmId, cropId),
    queryFn: () =>
      api
        .get<ListResp<SamplingResponse>>(`/api/v1/farms/${farmId}/samplings`, {
          params: { cycle_id: cropId, limit: '100' },
        })
        .then((r) => [...(r.data ?? [])].sort((a, b) => a.sampling_date.localeCompare(b.sampling_date))),
  });
}

export function useCropHarvests(farmId: string, cropId: string) {
  return useQuery({
    queryKey: ['harvests', cropId, 'cycle-list'],
    queryFn: () =>
      api.get<ListResp<HarvestResponse>>(`/api/v1/farms/${farmId}/harvests`, {
        params: { cycle_id: cropId, limit: '100' },
      }),
    select: (r) => [...(r.data ?? [])].sort((a, b) => a.harvest_date.localeCompare(b.harvest_date)),
  });
}

/** Per-day feed totals for one pond (the farm endpoint, filtered to the pond). */
export function usePondDailyFeed(farmId: string, pondId: string, start: string, end: string, enabled = true) {
  return useQuery({
    queryKey: ['feedings', farmId, 'daily-summary', start, end],
    queryFn: () =>
      api
        .get<ListResp<DailyFeedingSummary>>(`/api/v1/farms/${farmId}/feedings/daily-summary`, {
          params: { start_date: start, end_date: end },
        })
        .then((r) => r.data ?? []),
    select: (rows) => rows.filter((r) => r.pond_id === pondId),
    enabled,
  });
}

export type JournalPhase = 'prep' | 'grow_out';

interface PagedResp<T> {
  data: T[];
  meta?: { cursor?: string | null; has_more?: boolean | null; total?: number | null };
}

/** Newest first, paged by the journal's own keyset cursor (#206). */
export function useApplicationJournal(
  farmId: string,
  cropId: string,
  filter: { phase?: JournalPhase; category?: string },
  opts: { enabled?: boolean; pageSize?: number } = {}
) {
  const pageSize = opts.pageSize ?? 40;
  return useInfiniteQuery({
    queryKey: [...qk.applications.crop(farmId, cropId), 'journal', filter.phase ?? 'all', filter.category ?? 'all', pageSize],
    queryFn: ({ pageParam }) =>
      api.get<PagedResp<ApplicationResponse>>(`/api/v1/farms/${farmId}/applications`, {
        params: {
          cycle_id: cropId,
          sort: 'desc',
          limit: String(pageSize),
          ...(filter.phase ? { phase: filter.phase } : {}),
          ...(filter.category ? { product_category: filter.category } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    initialPageParam: '',
    getNextPageParam: (last) => (last.meta?.has_more && last.meta.cursor ? last.meta.cursor : undefined),
    enabled: opts.enabled ?? true,
  });
}

export function useApplicationSummary(farmId: string, cropId: string) {
  return useQuery({
    queryKey: [...qk.applications.crop(farmId, cropId), 'summary'],
    queryFn: () =>
      api
        .get<{ data: ApplicationSummary }>(`/api/v1/farms/${farmId}/applications/summary`, { params: { cycle_id: cropId } })
        .then((r) => r.data),
  });
}

export function useFarmProducts(farmId: string) {
  return useQuery({
    queryKey: qk.products.list(farmId),
    queryFn: () =>
      api.get<{ data: FarmProductResponse[] }>(`/api/v1/farms/${farmId}/products`).then((r) => r.data ?? []),
    staleTime: 60_000,
  });
}

function milestonesQuery(farmId: string, cropId: string) {
  return {
    queryKey: qk.milestones.crop(farmId, cropId),
    queryFn: () =>
      api
        .get<{ data: CycleMilestoneResponse[] }>(`/api/v1/farms/${farmId}/cycles/${cropId}/milestones`)
        .then((r) => r.data ?? []),
  };
}

export function useMilestones(farmId: string, cropId: string) {
  return useQuery(milestonesQuery(farmId, cropId));
}

/**
 * Upsert one milestone from a FRESH read: the PUT replaces every field, so a body
 * built from a cached list would revert planned_on/notes someone else changed since.
 */
export async function putMilestone(
  queryClient: QueryClient,
  farmId: string,
  cropId: string,
  kind: MilestoneKind,
  patch: Record<string, string | null>
) {
  const fresh = await queryClient.fetchQuery({ ...milestonesQuery(farmId, cropId), staleTime: 0 });
  const m = fresh.find((x) => x.kind === kind);
  return api.put(`/api/v1/farms/${farmId}/cycles/${cropId}/milestones/${kind}`, milestoneBody(m, patch));
}

export type MilestoneKind = CycleMilestoneResponse['kind'];

export const PREP_MILESTONES: Array<{ kind: MilestoneKind; label: string }> = [
  { kind: 'water_in', label: 'Water in' },
  { kind: 'culture_start', label: 'Culture started' },
  { kind: 'stocking_prep', label: 'Stocking prep' },
];

/** A milestone upsert body that keeps every field the caller doesn't patch (planned_on included). */
export function milestoneBody(m: CycleMilestoneResponse | undefined, patch: Record<string, string | null>) {
  return {
    done_on: m?.done_on ?? null,
    done_until: m?.done_until ?? null,
    planned_on: m?.planned_on ?? null,
    notes: m?.notes ?? null,
    ...patch,
  };
}

export interface PrepDay {
  date: string;
  /** 0 = before water-in. */
  day: number;
  milestones: string[];
  applications: ApplicationResponse[];
  checks: WaterCheckResponse[];
}

/** Prep journal days, newest first: only days with something logged, plus today while preparing. */
export function prepJournalDays(
  crop: Crop,
  milestones: CycleMilestoneResponse[],
  apps: ApplicationResponse[],
  preparing: boolean,
  checks: WaterCheckResponse[] = []
): PrepDay[] {
  const start = crop.prep_start_date!;
  let last = crop.stocking_date ? addPhDays(crop.stocking_date, -1) : todayInPH();
  // A check made the morning of stocking day, while the crop was still preparing, is a prep check.
  for (const c of checks) if (checkDay(c) > last) last = checkDay(c);
  const total = Math.max(daysBetween(start, last) + 1, 0);
  const days: PrepDay[] = [];
  for (let i = total - 1; i >= 0; i--) {
    const date = addPhDays(start, i);
    days.push({
      date,
      day: i + 1,
      milestones: milestones
        .filter((m) => m.done_on === date)
        .map((m) => PREP_MILESTONES.find((x) => x.kind === m.kind)?.label ?? m.kind),
      applications: apps.filter((a) => a.application_date === date),
      checks: checks.filter((c) => checkDay(c) === date),
    });
  }
  const beforeWaterIn = apps.filter((a) => a.application_date < start);
  const checksBeforeWaterIn = checks.filter((c) => checkDay(c) < start);
  if (beforeWaterIn.length > 0 || checksBeforeWaterIn.length > 0)
    days.push({
      date: beforeWaterIn[0]?.application_date ?? checkDay(checksBeforeWaterIn[0]),
      day: 0,
      milestones: [],
      applications: beforeWaterIn,
      checks: checksBeforeWaterIn,
    });
  const today = todayInPH();
  return days.filter((d) => d.milestones.length > 0 || d.applications.length > 0 || d.checks.length > 0 || (preparing && d.date === today));
}

/**
 * Out-of-range readings on an application's date and the day before, newest first, as
 * "why" reasons for it. Capped at 5 so a bad stretch doesn't bury the Routine/Other choices.
 */
export function useReadingReasons(farmId: string, cropId: string, date: string): string[] {
  const dayBefore = addPhDays(date, -1);
  const { data: onDate = [] } = useCropReadingsOn(farmId, cropId, date);
  const { data: onDayBefore = [] } = useCropReadingsOn(farmId, cropId, dayBefore);
  const dayBeforeLabel = format(parseISO(dayBefore), 'MMM d');
  const { table } = useFarmThresholds(farmId);
  const out: string[] = [];
  for (const [readings, suffix] of [
    [onDate, ''],
    [onDayBefore, ` ${dayBeforeLabel}`],
  ] as const) {
    const newestSlotFirst = [...readings].sort((a, b) => slotRank(b.reading_slot) - slotRank(a.reading_slot));
    for (const r of newestSlotFirst) {
      const v = readingToValues(r);
      for (const p of GRID_ROWS) {
        const n = v[p.dbColumn];
        if (p.pairSecondKey) {
          const m = v[paramByKey(p.pairSecondKey)!.dbColumn];
          const tier = p.key === 'alkalinity' ? alkalinityTier(table, n, m, v) : calMagTier(table, n, m, v);
          if (tier !== 'ok') out.push(`${p.label} ${formatPair(n, m)} at ${r.reading_slot}${suffix}`);
        } else if (typeof n === 'number' && tierFor(table, p.key, n, v) !== 'ok') {
          out.push(`${p.label} ${n} at ${r.reading_slot}${suffix}`);
        }
      }
    }
  }
  return out.slice(0, 5);
}

function slotRank(slot: string): number {
  return ALL_SLOTS.indexOf(slot as ReadingSlot);
}

/** Every prep-phase application of a crop. Prep is a bounded stretch, so all pages load. */
export function usePrepApplications(farmId: string, cropId: string) {
  const journal = useApplicationJournal(farmId, cropId, { phase: 'prep' }, { pageSize: 100 });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = journal;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  return {
    apps: journal.data?.pages.flatMap((p) => p.data) ?? [],
    loading: journal.isLoading || !!hasNextPage,
  };
}

interface EditHistoryList {
  data: { history: EditHistoryResponse[] };
}

/** Supervisor+ only (the API refuses everyone else). */
export function useApplicationHistory(farmId: string, applicationId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...qk.applications.crop(farmId, 'history'), applicationId],
    queryFn: () =>
      api
        .get<EditHistoryList>(`/api/v1/farms/${farmId}/edit-history/applications/${applicationId}`)
        .then((r) => r.data.history),
    enabled,
  });
}
